import { css } from '@emotion/react'
import { colors } from 'plume-ui'
import { useRef, useState } from 'react'
import { useUser } from '../../../../../contexts/AuthContext'
import { useOnClickOutside } from '../../../../../hooks/useOnClickOutside'
import {
  CONTACT_PERSON_SETTING_ON_STATUS_PANEL,
  SUM_OF_CONTACT_PERSON_SETTING,
  trackEvent,
} from '@/lib/tracking'
import { User } from '../../../../../types'
import { IcomoonIcon } from '../../../../atoms/IcomoonIcon'
import { CompanyContactPerson } from '../../../../../types/api'
import { StatusPanelSection } from './StatusPanelSection'

type Props = {
  shortname: string
  selectedContactPersonId?: number
  contactPeople: {
    userId: number
    imageUrl?: string
    firstName?: string
    lastName?: string
  }[]
  setSelectedContactPersonId: (id?: number) => void
  onSelectContactPerson: (
    companyContactPerson: CompanyContactPerson,
    user: User
  ) => void
  onSelectContactPersonBlank: (companyId: string, user: User) => void
}

export const ContactPersonSection: React.FC<Props> = ({
  shortname,
  selectedContactPersonId,
  contactPeople,
  setSelectedContactPersonId,
  onSelectContactPerson,
  onSelectContactPersonBlank,
}) => {
  const currentUser = useUser()

  const [contactPersonsOpening, setContactPersonsOpening] = useState(false)

  const ref = useRef<HTMLDivElement>(null)
  useOnClickOutside([ref], () => {
    setContactPersonsOpening(false)
  })

  const handleChangeCompanyContactPerson = async (contactPersonId: number) => {
    onSelectContactPerson(
      {
        companyId: shortname,
        userId: contactPersonId,
        imageUrl: selectContactPerson(contactPersonId)!.imageUrl,
        firstName: selectContactPerson(contactPersonId)!.firstName,
        lastName: selectContactPerson(contactPersonId)!.lastName,
      },
      currentUser
    )
    setSelectedContactPersonId(contactPersonId)
    trackEvent(currentUser, CONTACT_PERSON_SETTING_ON_STATUS_PANEL)
    trackEvent(currentUser, SUM_OF_CONTACT_PERSON_SETTING)
  }

  const handleDeleteCompanyContactPerson = () => {
    onSelectContactPersonBlank(shortname, currentUser)
    setSelectedContactPersonId()
    trackEvent(currentUser, CONTACT_PERSON_SETTING_ON_STATUS_PANEL)
    trackEvent(currentUser, SUM_OF_CONTACT_PERSON_SETTING)
  }

  const selectedContactPerson = contactPeople.find(
    (p) => p.userId === selectedContactPersonId
  )

  const selectContactPerson = (userId: number) =>
    contactPeople.find((p) => p.userId === userId)

  return (
    <StatusPanelSection label="担当者">
      <div
        css={css`
          position: relative;
        `}
        ref={ref}
      >
        <button
          css={css`
            display: flex;
            border: none;
            align-items: center;
            padding: 7px 4px 7px 10px;
            gap: 4px;
            border-radius: 3px;
            cursor: pointer;
            background-color: ${contactPersonsOpening
              ? colors.gray40
              : 'transparent'};
            &:hover {
              background-color: ${colors.gray40};
            }
            position: relative;
          `}
          onClick={() => setContactPersonsOpening(true)}
        >
          {selectedContactPerson ? (
            selectedContactPerson.imageUrl ? (
              <img
                alt="contact person icon"
                css={css`
                  width: 24px;
                  height: 24px;
                  object-fit: cover;
                  border-radius: 50%;
                  max-width: unset;
                `}
                src={selectedContactPerson.imageUrl}
              />
            ) : (
              <div
                css={css`
                  width: 24px;
                  height: 24px;
                  object-fit: cover;
                  border-radius: 50%;
                  max-width: unset;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  background-color: ${colors.blue50};
                  font-size: 11px;
                  color: ${colors.white00};
                `}
                className="contact-person-icon"
              >
                {selectedContactPerson.lastName?.slice(0, 2)}
              </div>
            )
          ) : (
            <div
              css={css`
                width: 24px;
                height: 24px;
                object-fit: cover;
                border-radius: 50%;
                max-width: unset;
                display: flex;
                justify-content: center;
                align-items: center;
                color: ${colors.white00};
                border: 1px dashed ${colors.gray70};
              `}
            >
              <IcomoonIcon
                name="person"
                css={css`
                  color: ${colors.gray70};
                  font-size: 16px;
                `}
              />
            </div>
          )}
          <div
            css={css`
              ${!selectedContactPerson && `color: ${colors.gray70}`};
              font-size: 14px;
            `}
            className="contact-person-value"
          >
            {selectedContactPerson
              ? `${selectedContactPerson.lastName} ${selectedContactPerson.firstName}`
              : '（空白）'}
          </div>
          <IcomoonIcon
            name="arrow1s-down"
            css={css`
              color: ${colors.black30};
              font-size: 20px;
            `}
          />
        </button>
        {contactPersonsOpening && (
          <ul
            css={css`
              width: 216px;
              max-height: 345px;
              margin-top: 7.5px;
              background: ${colors.white00};
              border-radius: 2px;
              box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
              padding: 5px 0;
              position: absolute;
              list-style: none;
              white-space: nowrap;
              transition: 0.2s;
              z-index: 50;
              overflow: auto;
              li:hover {
                background-color: ${colors.gray40};
              }
            `}
            className="contact-persons"
          >
            <li
              css={css`
                display: flex;
                flex-direction: row;
                align-items: center;
                line-height: 125%;
                letter-spacing: 0.02em;
                gap: 8px;
                font-size: 14px;
                color: ${colors.black30};
                cursor: pointer;
                padding: 7px 16px 7px 36px;
              `}
              onClick={async () => {
                await handleDeleteCompanyContactPerson()
                setContactPersonsOpening(false)
              }}
            >
              {selectedContactPersonId === undefined && (
                <IcomoonIcon
                  name={'check1'}
                  css={css`
                    position: absolute;
                    font-size: 12px;
                    left: 12px;
                    z-index: 1;
                    color: ${colors.black30};
                  `}
                />
              )}
              （空白）
            </li>
            {contactPeople.map((user) => (
              <li
                key={user.userId}
                css={css`
                  display: flex;
                  flex-direction: row;
                  align-items: center;
                  line-height: 125%;
                  letter-spacing: 0.02em;
                  gap: 8px;
                  font-size: 14px;
                  color: ${colors.black30};
                  cursor: pointer;
                  padding: 7px 16px 7px 36px;
                `}
                onClick={async () => {
                  await handleChangeCompanyContactPerson(user.userId)
                  setContactPersonsOpening(false)
                }}
              >
                {user.userId === selectedContactPersonId && (
                  <IcomoonIcon
                    name={'check1'}
                    css={css`
                      position: absolute;
                      font-size: 12px;
                      left: 12px;
                      z-index: 1;
                      color: ${colors.black30};
                    `}
                  />
                )}
                <div
                  className="contact-person-name"
                  css={css`
                    font-size: 14px;
                    color: ${colors.black30};
                    text-overflow: elipsis;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                  `}
                >{`${user.lastName} ${user.firstName}`}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </StatusPanelSection>
  )
}
